import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { subscribeNewsletter } from '../lib/api';
import { useT } from '../lib/i18n';

/**
 * Editorial newsletter signup — sits on the homepage between Featured and Locations.
 * Deliberately understated: eyebrow line, one sentence of copy, single email field,
 * quiet success feedback. No pop-up, no timer, no "join 5,000 subscribers" theatrics.
 */
const NewsletterSignup = () => {
  const { t, lang } = useT();
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || state === 'sending') return;
    setState('sending');
    setErrorMsg('');
    try {
      const { data } = await subscribeNewsletter(email.trim(), lang);
      setState(data.already_subscribed ? 'already' : 'success');
      setEmail('');
    } catch (err) {
      setErrorMsg(err?.response?.data?.detail || err.message);
      setState('error');
    }
  };

  return (
    <section
      className="py-24 px-6 bg-dark-green text-cream relative overflow-hidden"
      data-testid="newsletter-section"
    >
      {/* subtle art-deco border ornament */}
      <div className="absolute inset-x-6 top-6 h-px bg-gold/40"></div>
      <div className="absolute inset-x-6 bottom-6 h-px bg-gold/40"></div>

      <div className="max-w-3xl mx-auto text-center relative">
        <p
          className="text-xs tracking-[0.4em] text-gold uppercase mb-6"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t('newsletter.eyebrow')}
        </p>

        <h2
          className="text-4xl md:text-5xl font-bold mb-6"
          style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.05em' }}
        >
          {t('newsletter.title')}
        </h2>

        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-8 h-px bg-gold"></div>
          <div className="w-2 h-2 rotate-45 bg-dark-crimson border border-gold"></div>
          <div className="w-8 h-px bg-gold"></div>
        </div>

        <p
          className="text-cream/80 text-lg leading-relaxed max-w-xl mx-auto mb-10"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t('newsletter.body')}
        </p>

        {state === 'success' || state === 'already' ? (
          <p
            data-testid="newsletter-confirmation"
            className="text-gold text-xl italic min-h-[3rem]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t(state === 'success' ? 'newsletter.success' : 'newsletter.already')}
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row items-stretch gap-3 max-w-xl mx-auto"
            data-testid="newsletter-form"
          >
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('newsletter.placeholder')}
              disabled={state === 'sending'}
              data-testid="newsletter-email"
              className="flex-1 bg-cream/95 text-dark-green border-2 border-gold/60 rounded-none px-4 py-6 focus:border-gold focus:outline-none"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}
            />
            <Button
              type="submit"
              disabled={state === 'sending'}
              data-testid="newsletter-submit"
              className="bg-dark-crimson hover:bg-crimson text-cream px-8 py-6 rounded-none border-2 border-gold text-sm tracking-[0.2em] font-semibold disabled:opacity-60"
            >
              {state === 'sending' ? t('newsletter.subscribing') : t('newsletter.cta')}
            </Button>
          </form>
        )}

        {state === 'error' && (
          <p
            data-testid="newsletter-error"
            className="text-dark-crimson bg-cream/10 border border-dark-crimson px-4 py-2 mt-4 text-sm inline-block"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('newsletter.error')}
          </p>
        )}

        <p
          className="text-cream/50 text-xs tracking-wider mt-8"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {t('newsletter.privacy')}
        </p>
      </div>
    </section>
  );
};

export default NewsletterSignup;
